import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchAuthSession } from "aws-amplify/auth";
import NewspaperOutlinedIcon from "@mui/icons-material/NewspaperOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";

import "./intelligenceCenter.scss";

const EMPTY_METRICS = {
  monitoredGroups: 0,
  monitoredAssets: 0,
  newsToday: 0,
  commentsToday: 0,
  itemsForReview: 0,
  highestRiskScore: 0,
  highestRiskName: "No elevated person-level activity",
  highestRiskPerson: null,
};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatNumber = (value) =>
  new Intl.NumberFormat("en-US", {
    notation: toNumber(value) >= 10000 ? "compact" : "standard",
    maximumFractionDigits: 1,
  }).format(toNumber(value));

const formatRisk = (value) =>
  new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(toNumber(value));

const getGreeting = () => {
  const hour = new Date().getHours();

  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
};

const formatFetchTime = (date) => {
  if (!date) return "";

  return new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

const buildWorkspaceItems = (metrics) => [
  {
    title: "Monitoring",
    description:
      "Manage the protected people, organizations, topics, and locations included in your monitoring environment.",
    route: "/monitoring",
    icon: GroupsOutlinedIcon,
    meta: `${formatNumber(metrics.monitoredGroups)} active monitoring groups`,
  },
  {
    title: "News",
    description:
      "Review current reporting connected to protected individuals, organizations, topics, and locations.",
    route: "/news",
    icon: NewspaperOutlinedIcon,
    meta: `${formatNumber(metrics.newsToday)} articles identified today`,
  },
  {
    title: "Threat Feed",
    description:
      "Review collected discussions prioritized for potential protective-intelligence relevance.",
    route: "/threatFeed",
    icon: WarningAmberOutlinedIcon,
    meta: `${formatNumber(metrics.itemsForReview)} items identified for review`,
  },
  {
    title: "Analysis",
    description:
      "Access analyst determinations, documented concerns, and completed intelligence assessments.",
    route: "/assessments",
    icon: FactCheckOutlinedIcon,
    meta: "Open analyst review workspace",
  },
  {
    title: "Charts",
    description:
      "Examine activity levels, risk indicators, volume changes, and historical monitoring trends.",
    route: "/analytics",
    icon: BarChartOutlinedIcon,
    meta: `${formatNumber(metrics.commentsToday)} discussions collected today`,
  },
  {
    title: "Resources",
    description:
      "Access external research tools and sources used to support open-source intelligence collection.",
    route: "/resources",
    icon: ShieldOutlinedIcon,
    meta: "Open intelligence resources",
  },
];

export const IntelligenceCenter = () => {
  const navigate = useNavigate();
  const [userToken, setUserToken] = useState(null);

  const [dashboardData, setDashboardData] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [pageError, setPageError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [riskModalOpen, setRiskModalOpen] = useState(false);

  const getUserToken = async () => {
    try {
      const session = await fetchAuthSession();
      const accessToken = session.tokens?.accessToken?.toString();

      if (!accessToken) {
        navigate("/login");
        return;
      }

      setUserToken(accessToken);
    } catch (error) {
      console.error("Unable to retrieve authentication session:", error);
      navigate("/login");
    }
  };

  useEffect(() => {
    getUserToken();
  }, []);

  useEffect(() => {
    const fetchAssetStatistics = async () => {
      if (!userToken) return;

      setPageLoading(true);
      setPageError("");

      try {
        const response = await fetch(
          "https://api.weathervaneintel.com/api/api/assetStatistics",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify([
              {
                userToken,
                startDate: "",
              },
            ]),
          }
        );

        if (response.status === 403) {
          navigate("/login");
          return;
        }

        if (!response.ok) {
          throw new Error(
            `Asset statistics request failed with status ${response.status}`
          );
        }

        const data = await response.json();

        setDashboardData(data);
        setLastUpdated(new Date());
      } catch (error) {
        console.error("assetStatistics error:", error);
        setPageError(
          "The current monitoring summary could not be loaded. Your intelligence workspaces remain available."
        );
      } finally {
        setPageLoading(false);
      }
    };

    fetchAssetStatistics();
  }, [userToken]);

  useEffect(() => {
  if (!riskModalOpen) return undefined;

  const handleEscape = (event) => {
    if (event.key === "Escape") {
      closeRiskModal();
    }
  };

  document.addEventListener("keydown", handleEscape);
  document.body.style.overflow = "hidden";

  return () => {
    document.removeEventListener("keydown", handleEscape);
    document.body.style.overflow = "";
  };
}, [riskModalOpen]);

  const metrics = useMemo(() => {
    if (!dashboardData) return EMPTY_METRICS;

    const individualStats =
      dashboardData.comStatistics?.individual_stats || [];

    const monitoredAssets = individualStats.filter(
      (asset) => asset.assetType !== null
    );

    const monitoredPeople = individualStats.filter(
      (asset) => asset.assetType === "person"
    );

    const newsToday = (dashboardData.getNewsData || []).reduce(
      (total, group) => {
        const timeline = group.group_timeline_values || [];
        const latestValue = timeline[timeline.length - 1] || 0;
        return total + toNumber(latestValue);
      },
      0
    );

    const highestRiskPerson = monitoredPeople.reduce((highest, person) => {
      if (!highest) return person;

      return toNumber(person.risk_today) > toNumber(highest.risk_today)
        ? person
        : highest;
    }, null);

    const highestRiskName = highestRiskPerson
      ? `${highestRiskPerson.firstname || ""} ${highestRiskPerson.lastname || ""
        }`.trim()
      : "No elevated person-level activity";

return {
  monitoredGroups: dashboardData.getNewsData?.length || 0,
  monitoredAssets: monitoredAssets.length,
  newsToday,
  commentsToday: toNumber(dashboardData.getTotals?.comments_today),
  itemsForReview: toNumber(dashboardData.getTotals?.threats_today),
  highestRiskScore: toNumber(highestRiskPerson?.risk_today),
  highestRiskName,
  highestRiskPerson,
};
  }, [dashboardData]);

  const highestRiskTrend = useMemo(() => {
  const personId = metrics.highestRiskPerson?.id;

  if (!personId) return null;

  return (
    dashboardData?.assetTrends?.find(
      (trend) => Number(trend.id) === Number(personId)
    ) || null
  );
}, [dashboardData, metrics.highestRiskPerson]);

const highestRiskPerson = metrics.highestRiskPerson;

const personCommentsToday = toNumber(
  highestRiskPerson?.comments_today
);

const personCommentsThisWeek = toNumber(
  highestRiskPerson?.comments_last_week
);

const personThreatsToday = toNumber(
  highestRiskPerson?.threats_today
);

const personThreatsThisWeek = toNumber(
  highestRiskPerson?.threats_last_week
);

const personArticlesToday = toNumber(
  highestRiskPerson?.articles_today
);

const personArticlesThisWeek = toNumber(
  highestRiskPerson?.articles_last_week
);

const personRiskToday = toNumber(
  highestRiskPerson?.risk_today
);

const personRiskWeek = toNumber(
  highestRiskPerson?.risk_week
);

const personThreatRateToday =
  personCommentsToday > 0
    ? (personThreatsToday / personCommentsToday) * 100
    : 0;

const activityPercentChange = toNumber(
  highestRiskTrend?.percent_change
);

const activityContext =
  !highestRiskPerson
    ? "No person-level activity is currently available."
    : personCommentsToday === 0
      ? "No new discussion was collected for this person today."
      : activityPercentChange > 25
        ? "Discussion volume is currently above the recent daily average."
        : activityPercentChange < -25
          ? "Discussion volume is currently below the recent daily average."
          : "Discussion volume is generally consistent with the recent daily average.";

const riskContext =
  personRiskToday > personRiskWeek
    ? "Today's indicator is above this person's seven-day level."
    : personRiskToday < personRiskWeek
      ? "Today's indicator remains below this person's seven-day level."
      : "Today's indicator is consistent with this person's seven-day level.";

  const workspaceItems = useMemo(
    () => buildWorkspaceItems(metrics),
    [metrics]
  );

  const goTo = (route) => {
    if (pageLoading) return;
    navigate(route);
  };

const openRiskModal = () => {
  if (pageLoading || !metrics.highestRiskPerson) return;
  setRiskModalOpen(true);
};

const closeRiskModal = () => {
  setRiskModalOpen(false);
};


  if (userToken === null) {
    return (
      <main className="intelligence-center-page">
        <div
          className="intelligence-contour-background"
          aria-hidden="true"
        />

        <div className="intelligence-center-shell is-loading">
          <section className="intelligence-hero">
            <div className="intelligence-hero-copy">
              <span className="intelligence-eyebrow">
                Weathervane Intel
              </span>

              <h1>Intelligence Center</h1>

              <p>Loading your monitoring environment.</p>
            </div>
          </section>
        </div>
      </main>
    );
  }

  return (
    <main className="intelligence-center-page">
      <div className="intelligence-contour-background" aria-hidden="true" />

      <div
        className={`intelligence-center-shell ${pageLoading ? "is-loading" : "is-ready"
          }`}
      >
        <section className="intelligence-hero">
          <div className="intelligence-hero-copy">
            <span className="intelligence-eyebrow">Weathervane Intel</span>

            <h1>Intelligence Center</h1>

            <p>
              {getGreeting()}. Your monitoring environment is active and
              continues to identify reporting, online discussions, and
              potential indicators relevant to your protected assets.
            </p>

            <div className="intelligence-live-status">
              <span className="intelligence-live-dot" aria-hidden="true" />
              <span>{pageError ? "Summary unavailable" : "Monitoring active"}</span>

              <span className="intelligence-status-divider" aria-hidden="true">
                •
              </span>

              <span>
                {lastUpdated
                  ? `Summary refreshed at ${formatFetchTime(lastUpdated)}`
                  : pageLoading
                    ? "Retrieving current summary"
                    : "Workspace access remains available"}
              </span>
            </div>

            {pageError && <p role="alert">{pageError}</p>}
          </div>

          <div className="intelligence-hero-summary">
            <div className="intelligence-hero-summary-header">
              <ShieldOutlinedIcon aria-hidden="true" />
              <span>Monitoring Environment</span>
            </div>

            <div className="intelligence-hero-summary-grid">
              <div>
                <span>Active groups</span>
                <strong className={pageLoading ? "skeleton-value" : ""}>
                  {pageLoading ? "" : formatNumber(metrics.monitoredGroups)}
                </strong>
              </div>

              <div>
                <span>Monitored assets</span>
                <strong className={pageLoading ? "skeleton-value" : ""}>
                  {pageLoading ? "" : formatNumber(metrics.monitoredAssets)}
                </strong>
              </div>
            </div>
          </div>
        </section>

        <section
          className="intelligence-overview-section"
          aria-labelledby="daily-overview-heading"
        >
          <div className="intelligence-section-heading">
            <div>
              <span className="intelligence-section-kicker">
                Current Intelligence Picture
              </span>
              <h2 id="daily-overview-heading">Operational Overview</h2>
            </div>

            <p>
              A measured view of current collection activity, items that may
              merit review, and where analyst attention may be most useful.
            </p>
          </div>

          <div className="intelligence-overview-grid">
            <button
              type="button"
              className={`intelligence-overview-card ${pageLoading ? "is-skeleton" : ""
                }`}
              onClick={() => goTo("/news")}
            >
              <div className="intelligence-overview-icon">
                <NewspaperOutlinedIcon aria-hidden="true" />
              </div>

              <div className="intelligence-overview-content">
                <span>News identified today</span>
                <strong className={pageLoading ? "skeleton-value" : ""}>
                  {pageLoading ? "" : formatNumber(metrics.newsToday)}
                </strong>
                <small
                  className={
                    pageLoading ? "skeleton-line skeleton-line-wide" : ""
                  }
                >
                  {pageLoading
                    ? ""
                    : "Reporting connected to monitored people, topics, and locations"}
                </small>
              </div>

              <ArrowForwardOutlinedIcon
                className="intelligence-card-arrow"
                aria-hidden="true"
              />
            </button>

            <button
              type="button"
              className={`intelligence-overview-card ${pageLoading ? "is-skeleton" : ""
                }`}
              onClick={() => goTo("/threatFeed")}
            >
              <div className="intelligence-overview-icon">
                <WarningAmberOutlinedIcon aria-hidden="true" />
              </div>

              <div className="intelligence-overview-content">
                <span>Items for analyst review</span>
                <strong className={pageLoading ? "skeleton-value" : ""}>
                  {pageLoading ? "" : formatNumber(metrics.itemsForReview)}
                </strong>
                <small
                  className={
                    pageLoading ? "skeleton-line skeleton-line-wide" : ""
                  }
                >
                  {pageLoading
                    ? ""
                    : "Potentially relevant items identified for closer review"}
                </small>
              </div>

              <ArrowForwardOutlinedIcon
                className="intelligence-card-arrow"
                aria-hidden="true"
              />
            </button>

            <button
              type="button"
              className={`intelligence-overview-card ${pageLoading ? "is-skeleton" : ""
                }`}
              onClick={() => goTo("/threatFeed")}
            >
              <div className="intelligence-overview-icon">
                <BarChartOutlinedIcon aria-hidden="true" />
              </div>

              <div className="intelligence-overview-content">
                <span>Discussions collected today</span>
                <strong className={pageLoading ? "skeleton-value" : ""}>
                  {pageLoading ? "" : formatNumber(metrics.commentsToday)}
                </strong>
                <small
                  className={
                    pageLoading ? "skeleton-line skeleton-line-wide" : ""
                  }
                >
                  {pageLoading
                    ? ""
                    : "Total collection volume across the monitoring environment"}
                </small>
              </div>

              <ArrowForwardOutlinedIcon
                className="intelligence-card-arrow"
                aria-hidden="true"
              />
            </button>

<button
  type="button"
  className={`intelligence-overview-card ${
    pageLoading ? "is-skeleton" : ""
  }`}
  onClick={openRiskModal}
  aria-haspopup="dialog"
  aria-expanded={riskModalOpen}
  aria-controls="person-risk-modal"
>
              <div className="intelligence-overview-icon">
                <ShieldOutlinedIcon aria-hidden="true" />
              </div>

              <div className="intelligence-overview-content">
                <span>Highest person-level indicator</span>
                <strong className={pageLoading ? "skeleton-value" : ""}>
                  {pageLoading ? "" : formatRisk(metrics.highestRiskScore)}
                </strong>
                <small
                  className={
                    pageLoading ? "skeleton-line skeleton-line-wide" : ""
                  }
                >
                  {pageLoading
                    ? ""
                    : metrics.highestRiskScore > 0
                      ? `${metrics.highestRiskName} currently has the highest activity-based score`
                      : "No elevated person-level activity identified today"}
                </small>
              </div>

              <ArrowForwardOutlinedIcon
                className="intelligence-card-arrow"
                aria-hidden="true"
              />
            </button>
          </div>
        </section>

        <section
          className="intelligence-workspace-section"
          aria-labelledby="workspace-heading"
        >
          <div className="intelligence-section-heading">
            <div>
              <span className="intelligence-section-kicker">
                Platform Access
              </span>
              <h2 id="workspace-heading">Intelligence Workspace</h2>
            </div>

            <p>
              Continue directly to the area of the platform relevant to your
              current task.
            </p>
          </div>

          <div className="intelligence-workspace-grid">
            {workspaceItems.map((item) => {
              const Icon = item.icon;

              return (
                <button
                  type="button"
                  className={`intelligence-workspace-card ${pageLoading ? "is-skeleton" : ""
                    }`}
                  key={item.title}
                  onClick={() => goTo(item.route)}
                >
                  <div className="intelligence-workspace-topline">
                    <div className="intelligence-workspace-icon">
                      <Icon aria-hidden="true" />
                    </div>

                    <ArrowForwardOutlinedIcon
                      className="intelligence-card-arrow"
                      aria-hidden="true"
                    />
                  </div>

                  <div className="intelligence-workspace-copy">
                    <h3
                      className={
                        pageLoading
                          ? "skeleton-line skeleton-line-title"
                          : ""
                      }
                    >
                      {pageLoading ? "" : item.title}
                    </h3>
                    <p
                      className={
                        pageLoading
                          ? "skeleton-line skeleton-line-body"
                          : ""
                      }
                    >
                      {pageLoading ? "" : item.description}
                    </p>
                  </div>

                  <span
                    className={`intelligence-workspace-meta ${pageLoading
                        ? "skeleton-line skeleton-line-meta"
                        : ""
                      }`}
                  >
                    {pageLoading ? "" : item.meta}
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        <section className="intelligence-footer-status">
          <div>
            <span className="intelligence-footer-label">System status</span>
            <strong>
              {pageError
                ? "Monitoring summary unavailable"
                : "All core services operational"}
            </strong>
          </div>

          <div className="intelligence-footer-live">
            <span className="intelligence-live-dot" aria-hidden="true" />
            <span>
              {pageError
                ? "Workspace navigation remains available"
                : "Live monitoring enabled"}
            </span>
          </div>
        </section>
      </div>

      {riskModalOpen && (
  <div
    className="intelligence-modal-backdrop"
    onMouseDown={(event) => {
      if (event.target === event.currentTarget) {
        closeRiskModal();
      }
    }}
  >
    <section
      id="person-risk-modal"
      className="intelligence-risk-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="person-risk-modal-title"
      aria-describedby="person-risk-modal-description"
    >
      <header className="intelligence-risk-modal-header">
        <div>
          <span className="intelligence-section-kicker">
            Person-Level Monitoring Summary
          </span>

          <h2 id="person-risk-modal-title">
            {metrics.highestRiskName}
          </h2>

          <p id="person-risk-modal-description">
            Current activity associated with this monitored person,
            presented to support prioritization and further analyst
            review.
          </p>
        </div>

        <button
          type="button"
          className="intelligence-modal-close"
          onClick={closeRiskModal}
          aria-label="Close monitoring summary"
        >
          <CloseOutlinedIcon aria-hidden="true" />
        </button>
      </header>

      <div className="intelligence-risk-modal-status">
        <div className="intelligence-risk-score-block">
          <span>Current indicator</span>

          <strong>{formatRisk(personRiskToday)}</strong>

          <small>
            Seven-day indicator: {formatRisk(personRiskWeek)}
          </small>
        </div>

        <div className="intelligence-risk-context">
          <span>Monitoring assessment</span>

          <strong>{riskContext}</strong>

          <p>{activityContext}</p>
        </div>
      </div>

      <div className="intelligence-risk-modal-grid">
        <div className="intelligence-risk-detail-card">
          <span>Discussion collected today</span>
          <strong>{formatNumber(personCommentsToday)}</strong>
          <small>
            {formatNumber(personCommentsThisWeek)} collected during the
            last seven days
          </small>
        </div>

        <div className="intelligence-risk-detail-card">
          <span>Items identified today</span>
          <strong>{formatNumber(personThreatsToday)}</strong>
          <small>
            {formatNumber(personThreatsThisWeek)} identified during the
            last seven days
          </small>
        </div>

        <div className="intelligence-risk-detail-card">
          <span>Related reporting today</span>
          <strong>{formatNumber(personArticlesToday)}</strong>
          <small>
            {formatNumber(personArticlesThisWeek)} articles during the
            last seven days
          </small>
        </div>

        <div className="intelligence-risk-detail-card">
          <span>Review-item proportion</span>
          <strong>{personThreatRateToday.toFixed(1)}%</strong>
          <small>
            Share of today's collected discussion identified for closer
            review
          </small>
        </div>
      </div>

      <div className="intelligence-risk-modal-summary">
        <div>
          <span>Monitoring group</span>
          <strong>
            {highestRiskPerson?.group_name || "Not available"}
          </strong>
        </div>

        <div>
          <span>Discussion trend</span>
          <strong>
            {highestRiskTrend
              ? `${activityPercentChange > 0 ? "+" : ""}${activityPercentChange.toFixed(
                  1
                )}% from recent average`
              : "Trend not available"}
          </strong>
        </div>
      </div>

      <div className="intelligence-risk-modal-note">
        <ShieldOutlinedIcon aria-hidden="true" />

        <p>
          This indicator supports prioritization. It does not by itself
          establish intent, capability, credibility, or an imminent
          threat. Relevant items should be reviewed in context before an
          operational determination is made.
        </p>
      </div>

      <footer className="intelligence-risk-modal-actions">
        <button
          type="button"
          className="intelligence-modal-secondary"
          onClick={closeRiskModal}
        >
          Return to Intelligence Center
        </button>

        <button
          type="button"
          className="intelligence-modal-primary"
          onClick={() => {
            closeRiskModal();
            navigate("/analytics");
          }}
        >
          Open Analytical Dashboard
          <ArrowForwardOutlinedIcon aria-hidden="true" />
        </button>
      </footer>
    </section>
  </div>
)}
    </main>
  );
};
